/** Site Development Detail (Demo Mode only) - nested detail screen for a single development
 * project, opened by drilling into a row on the Site Development list (SiteDevelopment.tsx).
 * No backend site-development feed exists yet - all data is static mock data, matching the same
 * "seeded illustrative numbers" approach used elsewhere in src/pages/EstateManagement.
 * lazy: detail fields (checklist, zones, equipment, readiness, risks, documents) are
 * illustrative and shared across all projects rather than modelled per-project; only the
 * project name/type/stage/target open/status vary by row. Setup Checklist items are editable in
 * local state only - Save shows a confirmation snackbar rather than persisting, since there is
 * no site-development API yet (same pattern as EquipmentDetail.tsx). Upgrade path: back with a
 * real site-development project API response once the Site Development backend exists. */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, Grid, MenuItem, Snackbar, Stack, SvgIcon, Tab, Table,
  TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography, useTheme,
} from '@mui/material';
import { EventAvailable, FactCheckOutlined, ReportProblemOutlined, TrendingUpOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { MOCK_PROJECTS } from './SiteDevelopment';

// Illustrative full development plan, layered on top of whichever project row was selected.
const EXAMPLE = {
  status: 'Procurement',
  targetGoLive: '2026-08-01',
  owner: 'J. Okafor',
  customer: 'Ridgeway Council / Corporate Dining',
  siteType: 'Corporate Dining',
  serviceModel: 'Cook-fresh, servery + hospitality',
  expectedCovers: '450 covers/day',
  kitchenType: 'Production kitchen + servery',
  zonesRequired: 'Main Kitchen, Servery, Coffee Bar, Dry Store, Chilled Store, Dishwash',
  equipmentTemplate: 'Corporate Dining - Standard v3',
  readiness: 62,
};

const CHECKLIST = [
  { item: 'Site profile completed', done: true },
  { item: 'Operating model confirmed', done: true },
  { item: 'Zones defined', done: true },
  { item: 'Equipment requirements confirmed', done: true },
  { item: 'Equipment assigned', done: false },
  { item: 'Supplier setup completed', done: false },
  { item: 'Catalogue assignment completed', done: false },
  { item: 'Delivery schedule confirmed', done: false },
  { item: 'Compliance documents uploaded', done: false },
  { item: 'Staffing plan confirmed', done: false },
  { item: 'Reporting requirements confirmed', done: false },
  { item: 'Go-live approval completed', done: false },
];

const ZONES_LAYOUT = [
  { zone: 'Main Kitchen', type: 'Cooking / Preparation', status: 'Confirmed' },
  { zone: 'Servery', type: 'Serving', status: 'Confirmed' },
  { zone: 'Coffee Bar', type: 'Hospitality', status: 'Confirmed' },
  { zone: 'Dry Store', type: 'Storage', status: 'Confirmed' },
  { zone: 'Chilled Store', type: 'Storage', status: 'Pending layout sign-off' },
  { zone: 'Dishwash', type: 'Cleaning', status: 'Pending layout sign-off' },
];

const EQUIPMENT_REQUIREMENTS = [
  { equipment: 'Combi Oven (Rational iCombi Pro 10-1/1)', zone: 'Main Kitchen', qty: 1, status: 'Ordered' },
  { equipment: 'Fryer, Twin Basket', zone: 'Main Kitchen', qty: 1, status: 'Ordered' },
  { equipment: 'Walk-in Chiller', zone: 'Chilled Store', qty: 1, status: 'Awaiting quote' },
  { equipment: 'Espresso Machine', zone: 'Coffee Bar', qty: 1, status: 'Awaiting quote' },
];

const ASSIGNED_EQUIPMENT = [
  { equipment: 'Combi Oven (Rational iCombi Pro 10-1/1)', assetNumber: 'Pending install', status: 'Ordered' },
  { equipment: 'Fryer, Twin Basket', assetNumber: 'Pending install', status: 'Ordered' },
];

const READINESS_AREAS = [
  { area: 'Site profile & operating model', percent: 100 },
  { area: 'Zones & layout', percent: 100 },
  { area: 'Equipment requirements & assignment', percent: 60 },
  { area: 'Supplier & catalogue setup', percent: 30 },
  { area: 'Compliance & staffing', percent: 20 },
  { area: 'Go-live approval', percent: 0 },
];

const RISKS = [
  { risk: 'Espresso machine lead time may slip go-live date', owner: 'J. Okafor', due: '2026-07-18', status: 'Open' },
  { risk: 'Chilled store electrical supply needs upgrade', owner: 'Facilities', due: '2026-07-25', status: 'Open' },
];

const DOCUMENTS = [
  { name: 'Site Development Brief.pdf', category: 'Setup', uploaded: '2026-06-02' },
  { name: 'Equipment Requirements Schedule.xlsx', category: 'Equipment', uploaded: '2026-06-20' },
  { name: 'Kitchen Layout - Rev 3.pdf', category: 'Zones & Layout', uploaded: '2026-06-25' },
];

const STAGE_OPTIONS = ['Discovery', 'Design', 'Procurement', 'Installation', 'Testing', 'Ready for Launch', 'Live'];

interface SiteDevelopmentFormData {
  stage: string;
  targetOpen: string;
  owner: string;
  customer: string;
  siteType: string;
  serviceModel: string;
  expectedCovers: string;
  kitchenType: string;
  zonesRequired: string;
  equipmentTemplate: string;
}

const SiteDevelopmentDetail = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const [tabValue, setTabValue] = useState('overview');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [draftChecklist, setDraftChecklist] = useState(() => CHECKLIST.map((row) => ({ ...row })));

  const project = useMemo(
    () => MOCK_PROJECTS.find((row) => row.name === decodeURIComponent(id ?? '')) ?? MOCK_PROJECTS[0],
    [id],
  );

  const baselineFormData: SiteDevelopmentFormData = useMemo(() => ({
    stage: project.stage,
    targetOpen: project.targetOpen,
    owner: EXAMPLE.owner,
    customer: EXAMPLE.customer,
    siteType: EXAMPLE.siteType,
    serviceModel: EXAMPLE.serviceModel,
    expectedCovers: EXAMPLE.expectedCovers,
    kitchenType: EXAMPLE.kitchenType,
    zonesRequired: EXAMPLE.zonesRequired,
    equipmentTemplate: EXAMPLE.equipmentTemplate,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [project]);

  const [formData, setFormData] = useState<SiteDevelopmentFormData>(() => ({ ...baselineFormData }));

  const handleFieldChange = <K extends keyof SiteDevelopmentFormData>(field: K, value: SiteDevelopmentFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isDirty = JSON.stringify(draftChecklist) !== JSON.stringify(CHECKLIST)
    || JSON.stringify(formData) !== JSON.stringify(baselineFormData);

  const toggleChecklistItem = (index: number) => {
    setDraftChecklist((prev) => prev.map((row, i) => (i === index ? { ...row, done: !row.done } : row)));
  };

  const handleCancel = () => {
    setDraftChecklist(CHECKLIST.map((row) => ({ ...row })));
    setFormData({ ...baselineFormData });
  };

  const handleSave = () => {
    // lazy: no site-development API exists yet, so Save only confirms locally rather than persisting.
    setShowSuccessMessage(true);
  };

  const categories: { key: string; label: string; content: JSX.Element }[] = useMemo(() => [
    {
      key: 'overview',
      label: t('storeDevelopmentTabOverview', 'Overview'),
      content: (
        <>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mb: 3, display: 'block' }}>
            {t('storeDevelopmentExampleHint', 'Location Development is the mobilisation/change workflow for getting a location ready. Once live, the permanent operational record moves to Locations.')}
          </Typography>
          <Grid container
            spacing={4}>
            <Grid item
              xs={12}
              md={6}>
              <TextField label={t('storeDevelopmentSite', 'Location')}
                fullWidth
                disabled
                value={project.name} />
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <TextField select
                label={t('estateManagementStatus', 'Status')}
                fullWidth
                value={formData.stage}
                onChange={(e) => handleFieldChange('stage', e.target.value)}>
                {STAGE_OPTIONS.map((stage) => (
                  <MenuItem key={stage}
                    value={stage}>{stage}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <TextField label={t('storeDevelopmentTargetOpen', 'Target Open')}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.targetOpen}
                onChange={(e) => handleFieldChange('targetOpen', e.target.value)} />
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <TextField label={t('storeDevelopmentOwner', 'Mobilisation Owner')}
                fullWidth
                value={formData.owner}
                onChange={(e) => handleFieldChange('owner', e.target.value)} />
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <TextField label={t('storeDevelopmentCustomerContract', 'Client / Contract')}
                fullWidth
                value={formData.customer}
                onChange={(e) => handleFieldChange('customer', e.target.value)} />
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <TextField label={t('storeDevelopmentSiteType', 'Location Type')}
                fullWidth
                value={formData.siteType}
                onChange={(e) => handleFieldChange('siteType', e.target.value)} />
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <TextField label={t('storeDevelopmentServiceModel', 'Service Model')}
                fullWidth
                value={formData.serviceModel}
                onChange={(e) => handleFieldChange('serviceModel', e.target.value)} />
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <TextField label={t('storeDevelopmentExpectedCovers', 'Expected Covers / Volume')}
                fullWidth
                value={formData.expectedCovers}
                onChange={(e) => handleFieldChange('expectedCovers', e.target.value)} />
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <TextField label={t('storeDevelopmentKitchenType', 'Kitchen Type')}
                fullWidth
                value={formData.kitchenType}
                onChange={(e) => handleFieldChange('kitchenType', e.target.value)} />
            </Grid>
            <Grid item
              xs={12}
              md={6}>
              <TextField label={t('storeDevelopmentZonesRequired', 'Zones Required')}
                fullWidth
                value={formData.zonesRequired}
                onChange={(e) => handleFieldChange('zonesRequired', e.target.value)} />
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <TextField label={t('storeDevelopmentEquipmentTemplate', 'Equipment Model / Template')}
                fullWidth
                value={formData.equipmentTemplate}
                onChange={(e) => handleFieldChange('equipmentTemplate', e.target.value)} />
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <TextField label={t('storeDevelopmentReadinessPercent', 'Readiness')}
                fullWidth
                disabled
                value={`${EXAMPLE.readiness}%`} />
            </Grid>
          </Grid>
        </>
      ),
    },
    {
      key: 'setupChecklist',
      label: t('storeDevelopmentTabChecklist', 'Setup Checklist'),
      content: (
        <Table>
          <TableBody>
            {draftChecklist.map((row, index) => (
              <TableRow key={row.item}>
                <TableCell sx={{ width: 40 }}>
                  <Checkbox
                    checked={row.done}
                    onChange={() => toggleChecklistItem(index)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{row.item}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ),
    },
    {
      key: 'zonesLayout',
      label: t('storeDevelopmentTabZones', 'Zones & Layout'),
      content: (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('storeZonesName', 'Zone')}</TableCell>
              <TableCell>{t('siteZonesType', 'Type')}</TableCell>
              <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ZONES_LAYOUT.map((row) => (
              <TableRow key={row.zone}>
                <TableCell>{row.zone}</TableCell>
                <TableCell>{row.type}</TableCell>
                <TableCell>{row.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ),
    },
    {
      key: 'equipmentRequirements',
      label: t('storeDevelopmentTabEquipmentRequirements', 'Equipment Requirements'),
      content: (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('equipmentManagementAsset', 'Equipment')}</TableCell>
              <TableCell>{t('storeZonesName', 'Zone')}</TableCell>
              <TableCell>{t('storeDevelopmentQty', 'Qty')}</TableCell>
              <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {EQUIPMENT_REQUIREMENTS.map((row) => (
              <TableRow key={row.equipment}>
                <TableCell>{row.equipment}</TableCell>
                <TableCell>{row.zone}</TableCell>
                <TableCell>{row.qty}</TableCell>
                <TableCell>{row.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ),
    },
    {
      key: 'assignedEquipment',
      label: t('storeDevelopmentTabAssignedEquipment', 'Assigned Equipment'),
      content: (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('equipmentManagementAsset', 'Equipment')}</TableCell>
              <TableCell>{t('siteEquipmentAssetNumber', 'Asset Number')}</TableCell>
              <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ASSIGNED_EQUIPMENT.map((row) => (
              <TableRow key={row.equipment}>
                <TableCell>{row.equipment}</TableCell>
                <TableCell>{row.assetNumber}</TableCell>
                <TableCell>{row.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ),
    },
    {
      key: 'readiness',
      label: t('storeDevelopmentTabReadiness', 'Readiness'),
      content: (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('storeDevelopmentReadinessArea', 'Area')}</TableCell>
              <TableCell>{t('storeDevelopmentReadinessPercent', 'Readiness')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {READINESS_AREAS.map((row) => (
              <TableRow key={row.area}>
                <TableCell>{row.area}</TableCell>
                <TableCell>{row.percent}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ),
    },
    {
      key: 'risksActions',
      label: t('storeDevelopmentTabRisks', 'Risks & Actions'),
      content: (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('storeDevelopmentRisk', 'Risk / Blocker')}</TableCell>
              <TableCell>{t('storeDevelopmentOwner', 'Owner')}</TableCell>
              <TableCell>{t('storeDevelopmentDue', 'Due')}</TableCell>
              <TableCell>{t('estateManagementStatus', 'Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {RISKS.map((row) => (
              <TableRow key={row.risk}>
                <TableCell>{row.risk}</TableCell>
                <TableCell>{row.owner}</TableCell>
                <TableCell>{row.due}</TableCell>
                <TableCell>
                  <Chip label={row.status}
                    size="small"
                    color="warning"
                    variant="outlined" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ),
    },
    {
      key: 'documents',
      label: t('documents', 'Documents'),
      content: (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('storeDevelopmentDocumentName', 'Document')}</TableCell>
              <TableCell>{t('estateManagementCategory', 'Category')}</TableCell>
              <TableCell>{t('storeDevelopmentUploaded', 'Uploaded')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {DOCUMENTS.map((row) => (
              <TableRow key={row.name}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.category}</TableCell>
                <TableCell>{row.uploaded}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, project, draftChecklist, formData]);

  const doneCount = draftChecklist.filter((row) => row.done).length;
  const openRisksCount = RISKS.filter((row) => row.status === 'Open').length;

  const METRIC_TILES = [
    { key: 'readiness', label: t('storeDevelopmentReadinessPercent', 'Readiness'), value: `${EXAMPLE.readiness}%`, subtitle: t('storeDevelopmentReadinessSub', 'Overall project readiness'), icon: <TrendingUpOutlined />, color: '#1976d2' },
    { key: 'checklist', label: t('storeDevelopmentTabChecklist', 'Setup Checklist'), value: `${doneCount} / ${draftChecklist.length}`, subtitle: t('storeDevelopmentChecklistSub', 'Checklist items complete'), icon: <FactCheckOutlined />, color: '#2e7d32' },
    { key: 'risks', label: t('storeDevelopmentTabRisks', 'Risks & Actions'), value: `${openRisksCount}`, subtitle: t('storeDevelopmentRisksSub', 'Open risks and blockers'), icon: <ReportProblemOutlined />, color: '#d32f2f' },
    { key: 'targetOpen', label: t('storeDevelopmentTargetOpen', 'Target Open'), value: project.targetOpen, subtitle: t('storeDevelopmentOwner', 'Mobilisation Owner') + `: ${EXAMPLE.owner}`, icon: <EventAvailable />, color: '#ed6c02' },
  ];

  return (
    <PageArea>
      <Box sx={{ mb: 3 }}>
        <Chip label={t('storeDevelopmentBackToList', 'Back to Site Development')}
          size="small"
          variant="outlined"
          onClick={() => navigate('/estate-management/site-development')}
          sx={{ cursor: 'pointer' }} />
      </Box>

      <Box display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
        sx={{ mb: 6 }}>
        <PageTitle subTitle={t('storeDevelopmentTitle', 'Site Development')}>
          {project.name}
        </PageTitle>
        <Stack direction="row"
          spacing={3}>
          <Button variant="secondary"
            disabled={!isDirty}
            onClick={handleCancel}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button variant="primary"
            disabled={!isDirty}
            onClick={handleSave}>
            {t('save', 'Save')}
          </Button>
        </Stack>
      </Box>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {METRIC_TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.key}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Tabs
        value={tabValue}
        onChange={(_, value) => setTabValue(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: '-1px', borderBottom: 1, borderColor: 'divider', ml: 4, mr: 4, px: 2 }}
      >
        {categories.map((category) => (
          <Tab key={category.key}
            value={category.key}
            label={category.label} />
        ))}
      </Tabs>

      <Section style={{ mt: 0, p: 5, pb: 0 }}>
        <Grid container
          spacing={8}>
          {categories.map((category) => (
            <Grid item
              xs={12}
              key={category.key}
              sx={{ display: tabValue === category.key ? 'block' : 'none' }}>
              <Box sx={{ pb: 5 }}>
                {category.content}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Section>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSuccessMessage}
        autoHideDuration={4000}
        onClose={() => setShowSuccessMessage(false)}>
        <Alert onClose={() => setShowSuccessMessage(false)}
          severity="success"
          sx={{ width: '100%' }}>
          {t('storeDevelopmentSaved', 'Site development details saved.')}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default SiteDevelopmentDetail;
